"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGailDock } from "@/contexts/chat/GailDockContext";
import { resolveRouteFromPathname } from "@/data/routeRegistry";
import type { PageContext, PageEntityContext } from "@/services/wingman/types";

export type PageContextSupplier = () => PageEntityContext[] | null;

type PageContextValue = {
  registerPageContext: (supplier: PageContextSupplier) => () => void;
  getPageContext: () => PageContext | null;
  /** Corner GailGPT panel is open. The nav collapses and the next message includes this page. */
  assistantOverlayOpen: boolean;
  setAssistantOverlayOpen: (open: boolean) => void;
};

const PageContextContext = createContext<PageContextValue | undefined>(undefined);
// Page editors only register suppliers; they must not rerender their entire
// subtree whenever the assistant opens or closes.
const PageContextRegistrationContext = createContext<PageContextValue["registerPageContext"] | undefined>(undefined);

export const PageContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const suppliersRef = useRef<Set<PageContextSupplier>>(new Set());
  const [assistantOverlayOpen, setAssistantOverlayOpen] = useState(false);

  const { isGailGptDockEnabled } = useGailDock();
  const includePageContext = isGailGptDockEnabled || assistantOverlayOpen;

  const registerPageContext = useCallback((supplier: PageContextSupplier) => {
    suppliersRef.current.add(supplier);
    return () => {
      suppliersRef.current.delete(supplier);
    };
  }, []);

  const getPageContext = useCallback((): PageContext | null => {
    if (typeof window === "undefined") return null;
    if (!includePageContext) return null;

    const { pathname, search } = window.location;
    const route = resolveRouteFromPathname(pathname);

    const entities: PageEntityContext[] = [];
    for (const supplier of suppliersRef.current) {
      try {
        const supplied = supplier();
        if (supplied && supplied.length > 0) {
          entities.push(...supplied);
        }
      } catch {
        // A misbehaving page supplier must never block sending the message.
      }
    }

    return {
      url: `${pathname}${search}`,
      title: document.title || undefined,
      routeName: route?.name,
      routeDescription: route?.description,
      entities: entities.length > 0 ? entities : undefined,
    };
  }, [includePageContext]);

  const value = useMemo(
    () => ({
      registerPageContext,
      getPageContext,
      assistantOverlayOpen,
      setAssistantOverlayOpen,
    }),
    [registerPageContext, getPageContext, assistantOverlayOpen]
  );

  return (
    <PageContextContext.Provider value={value}>
      <PageContextRegistrationContext.Provider value={registerPageContext}>
        {children}
      </PageContextRegistrationContext.Provider>
    </PageContextContext.Provider>
  );
};

const NO_PAGE_CONTEXT: PageContextValue = {
  registerPageContext: () => () => {},
  getPageContext: () => null,
  assistantOverlayOpen: false,
  setAssistantOverlayOpen: () => {},
};

export const usePageContext = (): PageContextValue =>
  useContext(PageContextContext) ?? NO_PAGE_CONTEXT;

export const useRegisterPageContext = (
  supplier: PageContextSupplier
): void => {
  const registerPageContext = useContext(PageContextRegistrationContext) ?? NO_PAGE_CONTEXT.registerPageContext;
  const supplierRef = useRef(supplier);
  supplierRef.current = supplier;

  useEffect(() => {
    return registerPageContext(() => supplierRef.current());
  }, [registerPageContext]);
};
